import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import type {
  MarketplaceDefinitionConfig,
  PageAction,
} from '../types/marketplaceDefinition';

type PageActionsPageType = 'search_results' | 'product_listing';

interface PageActionsEditorProps {
  pageType: PageActionsPageType;
  config: MarketplaceDefinitionConfig;
  setConfig: Dispatch<SetStateAction<MarketplaceDefinitionConfig>>;
  getSelectorInputProps: (selectorValue: string | undefined) => Record<string, unknown>;
}

const PAGE_LABELS: Record<PageActionsPageType, string> = {
  search_results: 'search results',
  product_listing: 'product listing',
};

export default function PageActionsEditor({
  pageType,
  config,
  setConfig,
  getSelectorInputProps,
}: PageActionsEditorProps) {
  const page = config.pages?.[pageType];
  const actions: PageAction[] = (page as { page_actions?: PageAction[] } | undefined)?.page_actions || [];

  const updateActions = (next: PageAction[]) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageType]: {
          ...prev.pages?.[pageType],
          page_type: pageType,
          page_actions: next,
        },
      },
    }));
  };

  const updateAction = (idx: number, patch: Partial<PageAction>) => {
    const next = [...actions];
    next[idx] = { ...next[idx], ...patch };
    updateActions(next);
  };

  const removeAction = (idx: number) => {
    const next = [...actions];
    next.splice(idx, 1);
    updateActions(next);
  };

  const pageLabel = PAGE_LABELS[pageType];

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1">Page Actions</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            updateActions([...actions, { type: 'click', selector: '', wait_time: 1000 }])
          }
          size="small"
          sx={{ mr: 1 }}
        >
          Add Click Action
        </Button>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            updateActions([
              ...actions,
              {
                type: 'scroll',
                max_scrolls: 5,
                pause_ms: 1000,
                scroll_increment: 1000,
                stall_ms: 1000,
              },
            ])
          }
          size="small"
        >
          Add Scroll Action
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Actions to perform after page load and before field extraction (e.g., dismiss dialogs,
        expand &quot;More details&quot; panels on {pageLabel} pages, or scroll lazy-loaded search
        results).
      </Typography>

      {actions.map((action, idx) => (
        <Accordion key={idx} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              Action {idx + 1}: {action.type || 'click'}
              {action.type === 'scroll'
                ? ` (${action.max_scrolls || 5} scrolls)`
                : ` - ${action.selector || '(no selector)'}`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={action.type || 'click'}
                    onChange={e => updateAction(idx, { type: e.target.value as PageAction['type'] })}
                  >
                    <MenuItem value="click">click</MenuItem>
                    <MenuItem value="scroll">scroll</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: 'info.lighter',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'info.light',
                  }}
                >
                  <Typography variant="caption" color="info.dark">
                    {action.type === 'scroll' ? (
                      <>
                        <strong>scroll:</strong> Scroll the page to load lazy-loaded content before
                        parsing. Used for search results with infinite scroll.
                      </>
                    ) : (
                      <>
                        <strong>click:</strong> Click an element on the page after load. Use to
                        expand hidden sections (e.g. prom.ua &quot;More details&quot;), dismiss
                        dialogs, or accept cookies before scraping.
                      </>
                    )}
                  </Typography>
                </Box>
              </Grid>
              {action.type === 'scroll' ? (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max Scrolls"
                      value={action.max_scrolls ?? ''}
                      onChange={e =>
                        updateAction(idx, {
                          max_scrolls: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="5"
                      helperText="Maximum scroll iterations"
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Pause Between Scrolls (ms)"
                      value={action.pause_ms ?? ''}
                      onChange={e =>
                        updateAction(idx, {
                          pause_ms: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="1000"
                      helperText="Delay between scrolls"
                      inputProps={{ min: 0, step: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Scroll Increment (px)"
                      value={action.scroll_increment ?? ''}
                      onChange={e =>
                        updateAction(idx, {
                          scroll_increment: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="1000"
                      helperText="Pixels per scroll"
                      inputProps={{ min: 100, step: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Stall After Scroll (ms)"
                      value={action.stall_ms ?? ''}
                      onChange={e =>
                        updateAction(idx, {
                          stall_ms: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="1000"
                      helperText="Wait after scrolling"
                      inputProps={{ min: 0, step: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Item Selector (optional)"
                      value={action.item_selector || ''}
                      onChange={e =>
                        updateAction(idx, {
                          item_selector: e.target.value || undefined,
                        })
                      }
                      placeholder=".shopee-search-item-result"
                      helperText="CSS selector to count items"
                      InputProps={getSelectorInputProps(action.item_selector)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Max Items (optional)"
                      value={action.max_items ?? ''}
                      onChange={e =>
                        updateAction(idx, {
                          max_items: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      placeholder="50"
                      helperText="Stop when this many items loaded"
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Selector"
                      value={action.selector || ''}
                      onChange={e => updateAction(idx, { selector: e.target.value })}
                      placeholder={
                        pageType === 'product_listing'
                          ? "button[data-qaid='all_descriptions']"
                          : 'div.dialog-button, button.accept-cookies'
                      }
                      helperText="CSS selector for the element to click"
                      InputProps={getSelectorInputProps(action.selector)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Wait Time (ms)"
                      value={action.wait_time ?? 1000}
                      onChange={e => updateAction(idx, { wait_time: Number(e.target.value) })}
                      helperText="Wait after clicking (milliseconds)"
                      inputProps={{ min: 0, step: 100 }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => removeAction(idx)}
                >
                  Remove Action
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
